<template>
  <LoadingSpinner v-if="isPending" />

  <div v-else class="show-work-order no-print">
    <AppPageHeader :title="`WO# ${workOrder.workOrderId }`">
      <template #actions>
        <BaseDropdown
          title="Manage"
          right
          :items="[
            { title: 'Edit Work Order', to: { name: 'EditWorkOrder', params: { id: workOrder.id } } },
            { title: 'Email to Subcontractor', action: generateWorkOrderPdf },
            { title: 'Print', action: print },
            { title: 'Delete', action: () => showConfirmationModal = true }
          ]"
        />
      </template>
    </AppPageHeader>

    <!-- Work order detail -->
    <div class="grid grid-cols-6 gap-x-2 border-b border-slate-300 pb-4 mb-4">
      <div class="border-r border-slate-300">
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">Status</h3>
        <BaseBadge
          :variant="workOrder.status === WORK_ORDER_STATUS.CLOSED ? 'success' : 'warning'"
        >
          <span class="capitalize">{{ workOrder.status }}</span>
        </BaseBadge>
      </div>

      <div class="border-r border-slate-300">
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">Date</h3>
        <span class="text-sm font-weight-bold">{{ formatDate(workOrder.date) }}</span>
      </div>

      <div class="border-r border-slate-300">
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">WO #</h3>
        <span class="text-sm font-weight-bold">{{ workOrder.workOrderId }}</span>
      </div>

      <div class="border-r border-slate-300">
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">Project</h3>
        <span class="text-sm font-weight-bold">
          <router-link v-if="project && project?.id" :to="{ name: 'ShowProject', params: { projectId: project?.id, customerId: project?.customerId } }">
            {{ project?.name }}
          </router-link>
          <span v-else>{{ workOrder.projectId }}</span>
        </span>
      </div>

      <div class="border-r border-slate-300">
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">Subcontractor</h3>
        <span class="text-sm font-weight-bold">
          <router-link v-if="sub?.id" :to="{ name: 'ShowSubcontractor', params: { id: sub.id } }">
            {{ sub.name }}
          </router-link>
          <span v-else>{{ workOrder.subId }}</span>
        </span>
      </div>

      <div>
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">Total</h3>
        <span class="text-sm font-weight-bold">{{ currency(workOrder.total) }}</span>
      </div>
    </div>

    <!-- Work order items -->
    <BaseTable
      :items="workOrder.items"
      :fields="workOrderItemFields"
      class="mt-5"
    >
      <template #cost-data="{ row }">
        {{ currency(row.cost || 0) }}
      </template>
    </BaseTable>

    <hr class="border-b-slate-200 border-2" />

    <!-- totals -->
    <div class="mt-5 flex flex-col bg-slate-100 w-1/3 p-4 rounded-lg sm:ml-auto">
      <div class="flex justify-between mb-2">
        <span class="text-sm font-medium text-slate-500">Total</span>
        <span class="text-sm font-medium text-slate-500">{{ currency(workOrder.total) }}</span>
      </div>
    </div>

    <!-- <div class="confirmation">
      <SuccessModal :visible="showConfirmationModal">
        <template #body>
          <p>Your work order has been submited successfully. You can task the subcontractor with the WO number <b>{{ workOrder.workOrderId }}</b></p>
        </template>
        <template #confirm-button>
          <BaseButton
            block
            variant="primary"
            :to="{ hash: '#show-email' }"
          >
            Email
          </BaseButton>
        </template>
      </SuccessModal>
    </div> -->

    <DeleteConfirmationModal
      id="deleteConfirmationModal"
      v-model="showConfirmationModal"
      title="Delete Work Order"
      :is-deleting="deletingWO"
      @delete="deleteWorkOrder"
    >
      <div class="mt-2">
        <p class="text-base mb-3">Are you sure you want to delete this work order?</p>
        <p class="text-sm mb-2">This will also delete:</p>
        <ul class="text-sm list-disc ml-6">
          <li>All Bills (if they exist)</li>
        </ul>
      </div>
    </DeleteConfirmationModal>


    <Modal
      id="email-work-order"
      v-model="showEmailModal"
      title="Send Work Order"
      :no-escape="true"
      :no-backdrop="true"
      :no-close-button="true"
      width="w-full md:w-[80%] lg:max-w-5xl xl:max-w-6xl"
    >
      <EmailWorkOrder
        ref="emailWorkOrder"
        :work-order="workOrder"
        :subcontractor="sub"
        :pdf-link="pdfLink"
        :pdf-ready="pdfReady"
        :email="(email as Email)"
        @close="showEmailModal = false"
      />
    </Modal>

  </div>

  <div v-if="!isLoading && workOrder.id" class="no-print">
    <div
      v-show="!pdfReady"
      ref="workOrderPdf"
    >
      <WorkOrderToPdf
        :sub="sub"
        :work-order="workOrder"
        :project="project"
      />
    </div>
  </div>

  <div
    v-if="!isLoading && workOrder.id"
    id="printContent"
    class="hide-print-content"
  >
    <PrintWorkOrder
      :sub="sub"
      :work-order="workOrder"
      :project="project"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, useTemplateRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { WORK_ORDER_STATUS } from '@/models/workOrder'
import EmailWorkOrder from '../components/EmailWorkOrder.vue'
// import SuccessModal from '@/components/SucessModal.vue'
// import BaseButton from '@/components/base/BaseButton.vue'
import WorkOrderToPdf from '../components/WorkOrderToPdf.vue'
import html2pdf from 'html2pdf.js'
import DeleteConfirmationModal from '@/components/app/DeleteConfirmationModal.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import BaseTable from '@/components/base/BaseTable.vue'
import BaseBadge from '@/components/base/BaseBadge.vue'
import BaseDropdown from '@/components/base/BaseDropdown.vue'
import AppPageHeader from '@/components/app/AppPageHeader.vue'
import { useNotification } from '@kyvg/vue3-notification'
import WorkOrderService from '@/services/WorkOrderService'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import useGetAllSubs from '@/composables/useGetAllSubs'
import ProjectService from '@/services/ProjectService'
import { currency, formatDate } from '@/utils'
import { useHead } from '@unhead/vue'
import PrintWorkOrder from '../components/PrintWorkOrder.vue'
import Modal from '@/components/app/AppModal.vue'
import EmailService, { EmailTemplate } from '@/services/EmailService'
import { Email } from '@/models/email'
import Handlebars from 'handlebars'


const $route = useRoute()
const $router = useRouter()
const { notify } = useNotification()
const workOrderId = $route.params.id as string
const queryClient = useQueryClient()


const workOrderItemFields = [
  { key: 'name', label: 'Items' },
  { key: 'cost', label: 'Total' }
]

const showConfirmationModal = ref(false)
const showEmailModal = ref(false)
const pdfReady = ref(true)
const pdfLink = ref('')
const workOrderPdf = useTemplateRef('workOrderPdf')

const workOrderService = new WorkOrderService('')
const { data: workOrder, isPending } = useQuery({
  queryKey: ['workOrder', workOrderId],
  queryFn: async () => {
    return await workOrderService.find(workOrderId)
  }
})
useHead({ title: `Work Order ${workOrder.value?.workOrderId}` })

const { data: subcontractors } = useGetAllSubs()
const sub = computed(() => subcontractors.value?.find((sub) => sub.id === workOrder.value.subId))

const enableProjectQuery = computed(() => !!workOrder.value?.projectId)

const { data: project, isPending: isProjectPending } = useQuery({
  queryKey: ['project', workOrder.value?.projectId],
  queryFn: async () => {
    const projectService = new ProjectService()
    return await projectService.find(workOrder.value?.projectId)
  },
  enabled: enableProjectQuery
})

onMounted(() => {
  showConfirmationModal.value = $route.hash === '#show-confirmation'
  showEmailModal.value = $route.hash === '#show-email'
})

const print = () => {
  window.print()
}

const generateWorkOrderPdf = async () => {
  emailWorkOrder()
  showEmailModal.value = true
  pdfReady.value = false

  const opt = {
    margin: 0.5,
    filename: 'workOrder.pdf',
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
  }

  const attachment = await html2pdf().from(workOrderPdf.value).set(opt).toPdf().get('pdf').then((pdf) => {
    const blob = pdf.output('blob')
    pdfLink.value = URL.createObjectURL(blob)
    pdfReady.value = true
    return blob
  })

  const reader = new FileReader()
  reader.readAsDataURL(attachment)
  reader.onloadend = () => {
    const base64data = (reader.result as string).split(',')[1]
    email.value.message.attachments = [{
      filename: 'workOrder.pdf',
      content: base64data,
      encoding: 'base64'
    }]
    pdfReady.value = true
  }
}

const { mutateAsync: deleteWorkOrder, isPending: deletingWO } = useMutation({
  mutationFn: () => workOrderService.deleteWOWithBills(workOrder.value),
  onSuccess: () => {
    queryClient.removeQueries({ queryKey: ['workOrder', workOrder.value.id] })
    queryClient.removeQueries({ queryKey: ['WorkOrders', workOrder.value?.projectId], exact: false })
    queryClient.invalidateQueries({ queryKey: ['workOrders'] })
    queryClient.invalidateQueries({ queryKey: ['firestore', 'bills'], exact: false })
    $router.push({ name: 'ListWorkOrders' })
    notify({
      type: 'success',
      title: 'Work order deleted successfully'
    })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Failed to delete work order',
      text: error.message
    })
  },
})


const emailWorkOrder = () => {
  const link = `${window.location.origin}/workOrder/${workOrder.value.id}`
  const emailData = { subContractoName: sub.value.name, link: link }

  email.value.to = [sub.value.email]
  email.value.from = 'portal-admin@uttrly.com'

  email.value.message.subject = Handlebars.compile(emailTemplate.value.subject).call(this, { workOrderId: workOrder.value.workOrderId })

  Handlebars.registerPartial(baseTempalte.value.name, baseTempalte.value.html)
  email.value.message.html = Handlebars.compile(emailTemplate.value.html).call(this, emailData)
  showEmailModal.value = true
}

const email = ref({...new Email(), to: [], cc: []})
const emailService = new EmailService()

const { data: baseTempalte } = useQuery({
  queryKey: ['emailTemplates/base_layout'],
  queryFn: async () => {
    return await emailService.find('base_layout') || new EmailTemplate()
  }
})

const { data: emailTemplate } = useQuery({
  queryKey: ['emailTemplates/send_work_order'],
  queryFn: async () => {
    return await emailService.find('send_work_order') || new EmailTemplate()
  }
})

const isLoading = computed(() => isPending.value || isProjectPending.value || !sub.value?.id)

</script>

<style lang="scss" scoped>
@media print {
  .no-print {
    display: none;
  }
  #printContent {
    display: block;
  }
}

.hide-print-content {
  display: none;
}
</style>
